// Lib
import io from 'socket.io-client';
import HttpStatus from 'http-status-codes';
import { get, compact, isEmpty } from 'lodash/fp';

// Util
import {
    REFRESH_BOARD_ON_RECONNECT_ACTION_TYPES,
    cleanPersistedBuffer,
    cleanSendBufferBeforeFlush,
    debugSocketAction,
    debugSocketMaxRetryError,
    getResponseCode,
} from './socketClientUtils';
import getActionChannels from './getActionChannels';
import delay from '../../../common/utils/lib/delay';
import logger from '../logger';
import { fetchBoard } from '../../element/board/boardService';

// Socket throttling
import socketClientActionCounter from './socketClientActionCounter';
import { monitorLogAction } from '../../../server/common/monitoring/monitoringActions';

// Selectors
import { getCurrentBoardId } from '../../reducers/currentBoardId/currentBoardIdSelector';

// Actions
import { socketConnect, socketDisconnect, socketInterruption } from './socketActions';
import { errorModalOpen } from '../../components/error/modals/errorModalActions';

// Config
import getClientConfig from '../getClientConfig';

// Constants
import { SOCKET_ERROR } from './socketConstants';
import { MONITOR_ATTR } from '../../../server/common/monitoring/monitoringConstants';
import { SOCKET_EVENT_NAMES } from '../../../common/utils/socket/socketConstants';

const MAX_SOCKET_EMIT_RETRIES = 3;
const SOCKET_DISCONNECT_TIMEOUT = 20000;
const SOCKET_DISCONNECT_COUNT_TIMEOUT = 10000;

const clientConfig = getClientConfig();
const apiRoot = clientConfig.apiRoot || '/';

const createSocketClient = (userId) => {
    if (process.env.APPLICATION_MODE === 'electron' || process.env.APPLICATION_MODE === 'mobile-hybrid') {
        return io(apiRoot.replace('https', 'wss').replace('http', 'ws'), {
            query: `userId=${userId}`,
            transports: ['websocket', 'polling'],
        });
    }

    return io({ query: `userId=${userId}` });
};

class SocketManager {
    constructor(createSendBuffer) {
        this.socketClient = null;
        this.store = null;
        this.dispatch = null;
        this.isConnected = false;
        this.fetchCurrentBoardOnFlush = false;
        this.sendBuffer = cleanPersistedBuffer(createSendBuffer());
        this.userId = null;

        this.disconnectionCount = 0;
        this.disconnectionCountTimeoutId = null;

        this.disconnectionTimeoutId = null;
        this.getActionChannels = () => null;

        this.sentActionCounter = socketClientActionCounter();
        this.blockActions = false;
    }

    connectSocket(userId) {
        this.userId = userId || this.userId;

        // If there was an existing socket client, ensure all its event handlers get removed
        if (this.socketClient) this.socketClient.off();

        this.socketClient = createSocketClient(this.userId);

        // Incoming action handler
        this.socketClient.on(SOCKET_EVENT_NAMES.ACTION, (action) => this.dispatch(action));

        // Socket connection handler
        this.socketClient.on(SOCKET_EVENT_NAMES.CONNECT, () => {
            if (this.disconnectionTimeoutId) {
                clearTimeout(this.disconnectionTimeoutId);
                this.disconnectionTimeoutId = null;
            }

            const state = this.store.getState();
            this.updateChannels({
                joined: state.getIn(['app', 'socketConnection', 'channels']).toJS(),
                left: [],
            });

            cleanSendBufferBeforeFlush(this.sendBuffer);

            this.flushSendBuffer().then(() => {
                this.isConnected = true;
                this.dispatch(socketConnect());
                if (this.fetchCurrentBoardOnFlush) {
                    this.dispatch(
                        fetchBoard({
                            boardId: getCurrentBoardId(this.store.getState()),
                            loadAncestors: false,
                            force: true,
                        }),
                    );
                    this.fetchCurrentBoardOnFlush = false;
                }
            });
        });

        // Socket disconnection handler
        this.socketClient.on(SOCKET_EVENT_NAMES.DISCONNECT, (errorMessage) => {
            this.disconnectionCount++;

            // If a second disconnect occurs before the disconnection timeout, clear the timer
            if (this.disconnectionCountTimeoutId) clearTimeout(this.disconnectionCountTimeoutId);

            // If it's been 10 seconds since the last disconnection
            this.disconnectionCountTimeoutId = setTimeout(() => {
                this.disconnectionCount = 0;
                this.disconnectionCountTimeoutId = null;
            }, SOCKET_DISCONNECT_COUNT_TIMEOUT);

            const shouldClearSocketBuffer =
                this.disconnectionCount > 3 &&
                (errorMessage === 'transport close' || errorMessage === 'transport error') &&
                !this.sendBuffer.isEmpty();

            // This has been found to happen if a socket message exceeds the limit set on the socket server
            // e.g. "maxHttpBufferSize". Unfortunately the client won't receive information relating to what
            // actually went wrong, but due to the sendBuffer and retry mechanism, this will result in the
            // socket connecting and disconnecting many times in a short time period. Thus, we keep a count
            // of the number of disconnects within a 10 second period. If we do more than 3 and we have a
            // non-empty sendBuffer then it seems likely that the cause of the error is what's in the sendBuffer.
            // So we clear the send buffer and show the "save error" modal, so that the user will refresh their page.
            if (shouldClearSocketBuffer) {
                this.sendBuffer.clearBuffer();

                this.dispatch(
                    errorModalOpen({
                        modalId: SOCKET_ERROR.ACTION_FAILURE,
                        data: {
                            error: {
                                code: SOCKET_ERROR.ACTION_FAILURE,
                                message: 'Socket buffer failed to synchronise',
                            },
                            boardId: getCurrentBoardId(this.store.getState()),
                        },
                    }),
                );
            }

            this.dispatch(socketInterruption());

            this.isConnected = false;

            // If we've got an existing disconnection timeout that hasn't been cleared, don't start a new one
            if (this.disconnectionTimeoutId) return;

            // Mark the socket as disconnected if we have not reconnected within 20 seconds
            this.disconnectionTimeoutId = setTimeout(() => {
                this.dispatch(socketDisconnect());
            }, SOCKET_DISCONNECT_TIMEOUT);
        });
    }

    /**
     * Connect to all channels passed in here to ensure we can receive messages.
     * Only validate whether we should send messages when sending.
     */
    updateChannels({ joined, left }) {
        // Don't do anything if not joining or leaving anything
        if (isEmpty(joined) && isEmpty(left)) return;

        this.safeSocketEmit(SOCKET_EVENT_NAMES.UPDATE_CHANNELS, { joined, left });
    }

    disconnectSocket() {
        this.socketClient && this.socketClient.io.disconnect();
    }

    // TODO Could turn this into an async function if we updated electron to support it
    flushSendBuffer = () => {
        if (this.sendBuffer.isEmpty()) {
            this.sendBuffer.clearBuffer();
            return Promise.resolve();
        }

        const { id, eventName, action } = this.sendBuffer.getNextAction();

        action.bufferFlush = true;
        action.replay = true;

        action.monitoring = action.monitoring || {};
        action.monitoring[MONITOR_ATTR.REQUEST_MODE] = 'bufferFlush';

        if (REFRESH_BOARD_ON_RECONNECT_ACTION_TYPES[action.type]) {
            this.fetchCurrentBoardOnFlush = true;
        }

        return (
            this.rawSocketEmit({ eventName, action, actionId: id, isBufferFlush: true })
                // Skip errors so we try to at least send every event
                .catch(() => null)
                .then(this.flushSendBuffer)
        );
    };

    emitAction(syncAction) {
        const channels = compact(this.getActionChannels(syncAction));

        // if there are no valid channels to send the action to, don't send it!
        if (!channels.length) return;

        this.safeSocketEmit(SOCKET_EVENT_NAMES.ACTION, {
            ...syncAction,
            channels,
        });
    }

    /**
     * Saves the action to a send buffer before emitting it via the socket.
     * This will ensure that the action will not be lost until an acknowledgement is received.
     */
    safeSocketEmit(eventName, action) {
        // TODO - Enable this when socket throttling logic has been validated
        // if (this.blockActions) return;

        const actionId = this.sendBuffer.addActionToBuffer(eventName, action);

        if (!this.isConnected) return;

        return this.rawSocketEmit({ eventName, action, actionId });
    }

    /**
     * Sends an action via the socket client and removes the action from the send buffer when
     * an "OK" acknowledgement is received.
     * Otherwise retries on error, until a maximum number of retries, then log the error and
     * remove the action from the send buffer.
     */
    rawSocketEmit = ({ eventName, action, actionId, attempt = 0, isBufferFlush }) =>
        new Promise((resolve, reject) => {
            // Don't count buffer flushes within the throttled count
            if (!isBufferFlush) {
                this.sentActionCounter.incrementSentActionCount(action);

                if (!this.sentActionCounter.isWithinCountLimits(action)) {
                    logger.error('Action count limit exceeded', action.type);

                    // TODO - Enable this when socket throttling logic has been validated
                    // this.blockActions = true;

                    const monitoringAction = monitorLogAction({
                        user: action.user,
                        operation: 'SOCKET_THROTTLE',
                        actionType: action.type,
                    });
                    this.socketClient.emit(SOCKET_EVENT_NAMES.ACTION, monitoringAction);

                    // TODO - Enable this when socket throttling logic has been validated
                    // this.dispatch(errorModalOpen({
                    //     modalId: SOCKET_ERROR.ACTION_LIMIT_EXCEEDED,
                    //     data: {
                    //         error: {
                    //             code: SOCKET_ERROR.ACTION_LIMIT_EXCEEDED,
                    //             message: `Client socket action limit exceeded (${get('type', action)})`,
                    //         },
                    //         action,
                    //         boardId: getCurrentBoardId(this.store.getState()),
                    //     },
                    // }));
                    //
                    // return;
                }
            }

            this.socketClient.emit(eventName, action, async (response) => {
                debugSocketAction(eventName, action);

                const responseCode = getResponseCode(response);

                if (responseCode === HttpStatus.OK) {
                    this.sendBuffer.removeActionFromBuffer(actionId);
                    return resolve();
                }

                const isRetriable = !responseCode || responseCode >= 500;

                if (isRetriable && attempt < MAX_SOCKET_EMIT_RETRIES) {
                    await delay(Math.pow(attempt + 1, 2) * 100);

                    // Add properties onto the event so the server knows it's a replay
                    action.replay = true;
                    action.attempt = attempt + 1;

                    action.monitoring = action.monitoring || {};
                    action.monitoring[MONITOR_ATTR.REQUEST_MODE] = 'retry';

                    return this.rawSocketEmit({ eventName, action, actionId, attempt: attempt + 1 }).then(resolve);
                }

                debugSocketMaxRetryError(eventName, action, response);

                this.sendBuffer.removeActionFromBuffer(actionId);

                // TODO Add to failed actions?
                this.dispatch(
                    errorModalOpen({
                        modalId: SOCKET_ERROR.ACTION_FAILURE,
                        data: {
                            error: {
                                code: SOCKET_ERROR.ACTION_FAILURE,
                                message: `${responseCode}: Client socket action error (${get('type', action)})`,
                            },
                            action,
                            boardId: getCurrentBoardId(this.store.getState()),
                        },
                    }),
                );

                return resolve();
            });
        });

    subscribeToStore(store) {
        this.store = store;
        this.dispatch = this.store.dispatch.bind(this.store);
        this.getActionChannels = getActionChannels(this.store);
    }
}

export const getSocketManager = (createSendBuffer) => new SocketManager(createSendBuffer);
